import { useCallback, useState } from 'react';
import { FunctionsHttpError } from '@qc/db';
import {
  bevestigRegel,
  controleerKraamgegevens,
  isKlaarVoorGoedkeuring,
  leesConcept,
  LEGE_KRAAMGEGEVENS,
  watMoetNagekeken,
  type Kraamgegevens,
  type KraamgegevensVeld,
  type Menuconcept,
  type Menuconceptregel,
  type RuweRegel,
} from '@qc/core';
import { supabase } from '../supabase.js';
import { maakKlaarVoorInlezen } from './bestandlezen.js';

/**
 * De echte melding uit een mislukte edge-function-aanroep.
 *
 * `supabase.functions.invoke` gooit bij een non-2xx-status alles in `error` en
 * laat `data` op null staan — de nette Nederlandse tekst die de functie
 * teruggaf (503 "nog niet aan", 403 "code kennen we niet", enzovoort) zit dan
 * in `error.context`, een ruwe Response. Zonder dit uit te lezen krijgt de
 * kraam bij elke fout dezelfde generieke zin, ook als de echte reden allang
 * bekend en vriendelijk geformuleerd was.
 */
async function leesEdgeFoutmelding(fout: unknown): Promise<string | null> {
  if (!(fout instanceof FunctionsHttpError)) return null;
  try {
    const body: unknown = await fout.context.json();
    if (body !== null && typeof body === 'object' && 'melding' in body) {
      const melding = (body as { melding: unknown }).melding;
      if (typeof melding === 'string') return melding;
    }
  } catch {
    // De body was geen JSON of al gelezen; dan valt terug op de generieke tekst.
  }
  return null;
}

export interface Kraaminfo {
  kraamdeelnameId: string;
  kraamnaam: string;
  evenementnaam: string;
  bekerborgCent: number;
  /** Wat de kraam al heeft ingevuld voor factuur en uitbetaling. */
  gegevens: Kraamgegevens;
}

export interface GegevensUitslag {
  ok: boolean;
  fouten: Partial<Record<KraamgegevensVeld, string>>;
  melding: string | null;
}

export interface MenuInsturen {
  kraam: Kraaminfo | null;
  /** Slaat IBAN, KvK, BTW-nummer en contact op; per veld een fout als iets niet klopt. */
  bewaarGegevens: (code: string, invoer: Kraamgegevens) => Promise<GegevensUitslag>;
  gegevensBezig: boolean;
  concept: Menuconcept;
  bezig: boolean;
  fout: string | null;
  ingestuurd: { versie: number; aantal: number } | null;
  zoekKraam: (code: string) => Promise<boolean>;
  voegRegelToe: () => void;
  wijzigRegel: (index: number, wijziging: Partial<Menuconceptregel>) => void;
  verwijderRegel: (index: number) => void;
  zetConcept: (concept: Menuconcept) => void;
  leesKaart: (code: string, bestand: File) => Promise<string | null>;
  leest: boolean;
  stuurIn: (code: string) => Promise<void>;
  nogNaTeKijken: Menuconceptregel[];
  magInsturen: boolean;
}

const LEGE_REGEL: Menuconceptregel = {
  naam: '',
  prijsCent: null,
  btwTarief: null,
  categorie: 'Overig',
  bekers: 0,
  onzeker: ['naam', 'prijs', 'btw'],
};

export function useMenuInsturen(): MenuInsturen {
  const [kraam, setKraam] = useState<Kraaminfo | null>(null);
  const [concept, setConcept] = useState<Menuconcept>(leesConcept([]));
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [ingestuurd, setIngestuurd] = useState<{ versie: number; aantal: number } | null>(null);
  const [leest, setLeest] = useState(false);

  const zoekKraam = useCallback(async (code: string) => {
    setBezig(true);
    setFout(null);

    const { data, error } = await supabase.rpc('kraam_op_inlogcode', { code });
    setBezig(false);

    if (error) {
      setFout('We konden je kraam niet opzoeken. Probeer het zo nog eens.');
      return false;
    }
    if (data == null) {
      setFout('Deze code kennen we niet. Kijk even op het kaartje uit de doos.');
      return false;
    }

    const gevonden = data as {
      kraamdeelname_id: string;
      kraamnaam: string;
      evenementnaam: string;
      bekerborg_cent: number;
      gegevens?: {
        iban: string;
        kvk_nummer: string;
        btw_nummer: string;
        contactpersoon: string;
        telefoon: string;
        email: string;
        selfbilling_akkoord: boolean;
      } | null;
    };
    const g = gevonden.gegevens;
    setKraam({
      kraamdeelnameId: gevonden.kraamdeelname_id,
      kraamnaam: gevonden.kraamnaam,
      evenementnaam: gevonden.evenementnaam,
      bekerborgCent: gevonden.bekerborg_cent,
      gegevens: g
        ? {
            iban: g.iban,
            kvkNummer: g.kvk_nummer,
            btwNummer: g.btw_nummer,
            contactpersoon: g.contactpersoon,
            telefoon: g.telefoon,
            email: g.email,
            selfbillingAkkoord: g.selfbilling_akkoord,
          }
        : LEGE_KRAAMGEGEVENS,
    });
    return true;
  }, []);

  const [gegevensBezig, setGegevensBezig] = useState(false);

  const bewaarGegevens = useCallback(
    async (code: string, invoer: Kraamgegevens): Promise<GegevensUitslag> => {
      const controle = controleerKraamgegevens(invoer);
      if (!controle.geldig) return { ok: false, fouten: controle.fouten, melding: null };

      const g = controle.genormaliseerd;
      setGegevensBezig(true);
      const { error } = await supabase.rpc('zet_kraamgegevens_op_inlogcode', {
        code,
        gegevens: {
          iban: g.iban,
          kvk_nummer: g.kvkNummer,
          btw_nummer: g.btwNummer,
          contactpersoon: g.contactpersoon,
          telefoon: g.telefoon,
          email: g.email,
          selfbilling_akkoord: g.selfbillingAkkoord,
        },
      });
      setGegevensBezig(false);

      if (error) return { ok: false, fouten: {}, melding: error.message };
      setKraam((huidig) => (huidig === null ? huidig : { ...huidig, gegevens: g }));
      return { ok: true, fouten: {}, melding: null };
    },
    [],
  );

  const voegRegelToe = useCallback(() => {
    setConcept((huidig) => ({ ...huidig, regels: [...huidig.regels, { ...LEGE_REGEL }] }));
  }, []);

  const wijzigRegel = useCallback((index: number, wijziging: Partial<Menuconceptregel>) => {
    setConcept((huidig) => ({
      ...huidig,
      regels: huidig.regels.map((regel, i) =>
        i === index ? bevestigRegel(regel, wijziging) : regel,
      ),
    }));
  }, []);

  const verwijderRegel = useCallback((index: number) => {
    setConcept((huidig) => ({
      ...huidig,
      regels: huidig.regels.filter((_, i) => i !== index),
    }));
  }, []);

  /**
   * De geüploade kaart laten uitlezen.
   *
   * Dit gaat via een edge function en niet rechtstreeks: de sleutel van de
   * API hoort op de server, want alles wat in deze app staat kan iedereen
   * lezen die het scherm opent.
   *
   * Wat eruit komt is een voorstel. Elke regel die het model niet zeker wist
   * blijft rood tot de kraam hem bevestigt, en lukt het inlezen niet, dan typt
   * de kraam gewoon door — dat is de reden dat dit een aanvulling is en geen
   * voorwaarde. Geeft de melding terug die de kraam te zien krijgt.
   */
  const leesKaart = useCallback(async (code: string, bestand: File) => {
    setLeest(true);
    setFout(null);

    try {
      const klaar = await maakKlaarVoorInlezen(bestand);
      const { data, error } = await supabase.functions.invoke('lees-menukaart', {
        body: { code, bestand: klaar.data, mediaType: klaar.mediaType },
      });

      if (error !== null || data == null) {
        const echteMelding = await leesEdgeFoutmelding(error);
        return (
          echteMelding ??
          'We konden je kaart niet uitlezen. Type je producten er hieronder even bij.'
        );
      }

      const uitslag = data as {
        regels?: unknown;
        overgeslagen?: unknown;
        melding?: unknown;
      };

      if (typeof uitslag.melding === 'string') return uitslag.melding;
      if (!Array.isArray(uitslag.regels) || uitslag.regels.length === 0) {
        return 'We vonden geen producten op deze kaart. Type ze er hieronder even bij.';
      }

      const overgeslagen = Array.isArray(uitslag.overgeslagen)
        ? uitslag.overgeslagen.filter((regel): regel is string => typeof regel === 'string')
        : [];

      const gelezen = leesConcept(uitslag.regels as RuweRegel[], overgeslagen);

      // Wat de kraam zelf al had ingetypt blijft staan. Hij mag intypen terwijl
      // het lezen loopt, en dan is zijn werk kwijtraken het ergste wat er kan
      // gebeuren — erger dan een regel die dubbel staat.
      setConcept((huidig) => ({
        regels: [...huidig.regels.filter((regel) => regel.naam.trim() !== ''), ...gelezen.regels],
        overgeslagen: gelezen.overgeslagen,
      }));

      const aantal = uitslag.regels.length;
      return `We lazen ${aantal} ${aantal === 1 ? 'product' : 'producten'} van je kaart. Kijk ze na — de rode velden weten we niet zeker.`;
    } catch {
      return 'We konden je kaart niet uitlezen. Type je producten er hieronder even bij.';
    } finally {
      setLeest(false);
    }
  }, []);

  const stuurIn = useCallback(
    async (code: string) => {
      setBezig(true);
      setFout(null);

      const regels = concept.regels.map((regel) => ({
        naam: regel.naam,
        prijs_cent: regel.prijsCent ?? 0,
        btw_tarief: regel.btwTarief ?? 900,
        categorie: regel.categorie,
        bekers: regel.bekers,
        onzeker: regel.onzeker.length > 0,
      }));

      const { data, error } = await supabase.rpc('stuur_menu_in', { code, regels });
      setBezig(false);

      if (error) {
        setFout(error.message);
        return;
      }
      const uitslag = data as { versie: number; aantal: number } | null;
      setIngestuurd(uitslag ?? { versie: 1, aantal: regels.length });
    },
    [concept],
  );

  return {
    kraam,
    bewaarGegevens,
    gegevensBezig,
    concept,
    bezig,
    fout,
    ingestuurd,
    zoekKraam,
    voegRegelToe,
    wijzigRegel,
    verwijderRegel,
    zetConcept: setConcept,
    leesKaart,
    leest,
    stuurIn,
    nogNaTeKijken: watMoetNagekeken(concept),
    magInsturen: isKlaarVoorGoedkeuring(concept),
  };
}
